import { storefront } from '@/storefront';
import {IEditorImageElement, IEditorPhotoElement, IEditorTextElement, Imports, IEditorPage} from "./base/editor.interfaces";
import {IEditorElementError, IEditorErrorConfiguration, IEditorUtil} from "@/customizer/interfaces";



const globalImports: Imports = typeof global !== 'undefined' ? global as any : window as any;

const SF = globalImports.SF || (globalImports.SF = {});
SF.Model = SF.Model || {} as any;

const elementResolution = {
    OK: 0,
    WARNING: 1,
    ERROR: 2
};

function elementError(type:number, page:number, label:string, extras?:any):IEditorElementError {
    return {
        type: type,
        page: page,
        label: label,
        extra: extras || null
    };
}

export class ErrorDetector {
    public readonly elementResolution:{OK:number, WARNING: number, ERROR: number} = elementResolution;

    public getErrorsInPages(pages:IEditorPage[]):IEditorElementError[] {

        return pages
            .map((page:IEditorPage):IEditorElementError[] => {
                return this.getErrorsInPage(page, pages.length);
            })
            .reduce((a:IEditorElementError[], b:IEditorElementError[]):IEditorElementError[] => {
                return a.concat(b);
            }, []);
    }

    public getErrorsInPage(page:IEditorPage, pagesLength:number, errorConfiguration?:IEditorErrorConfiguration):IEditorElementError[] {
        // if page is not editable, there is no reason we should be looking for errors

        if (!page.canEdit) {
            return [];
        }

        const errors = [];
        let warning;

        // a counter for the number of missing photos on this page
        let missingPhotos = 0;
        if (page.elements.length == 0 && page.type == 'page') {
            if (pagesLength == 1) {
                missingPhotos++;
            }

        }
        // loop over each freeObject make sure that if it's an image, then it is not missing, and it doesn't have a resolution warning
        // additionally it also ensures text is not the default.
        for (let i = 0; i < page.elements.length; i++) {
            const element = page.elements[i];

            if(!element.editable) {
                // un-editable elements cannot have errors
                continue;
            }

            // if we have a photo or a background
            if (element instanceof SF.Model.PhotoElement || element instanceof SF.Model.BackgroundElement) {

                // if it doesn't have an image, mark as such
                if (!(element as IEditorImageElement).image) {
                    missingPhotos++;
                } else if (errorConfiguration) {
                    // it has an image, does it have a resolution warning?
                    warning = this.elementHasResolutionWarning(element as IEditorPhotoElement, errorConfiguration);

                    if (warning > 0 && element.image) {
                        errors.push(this.createError(SF.Model.ElementErrorType.RESOLUTION, page, {
                            severity: warning,
                            photoId: (element as any).image.id
                        }));
                    }
                }
            }

            // if we have a text object
            if (element instanceof SF.Model.TextElement && ErrorDetector.isAnUneditedText(element as IEditorTextElement)) {
                errors.push(this.createError(SF.Model.ElementErrorType.TEXT, page));
            }
        }

        if (missingPhotos > 0) {
            errors.push(this.createError(SF.Model.ElementErrorType.MISSING_PHOTO, page, {count: missingPhotos}));
        }

        return errors;
    }

    private createError(type:number, page:IEditorPage, extras?:any):IEditorElementError {
        return elementError(type, page.index, page.getLabel(), extras);
    }

    private elementHasResolutionWarning(element:IEditorPhotoElement, errorConfiguration:IEditorErrorConfiguration):number {
        if (element.imagePosition.zoom * element.imagePosition.width > storefront.editor.MAX_IMAGE_SIZE ||
            element.imagePosition.zoom * element.imagePosition.height > storefront.editor.MAX_IMAGE_SIZE) {
            return elementResolution.ERROR;
        }
        if (element.imagePosition.zoom * element.imagePosition.width > storefront.editor.MAX_IMAGE_SIZE * .9 ||
            element.imagePosition.zoom * element.imagePosition.height > storefront.editor.MAX_IMAGE_SIZE * .9) {
            return elementResolution.WARNING;
        }
        const zoom = element.imagePosition.zoom;
        if (zoom < errorConfiguration.zoomWarning) {
            return elementResolution.OK;
        } else if (zoom < errorConfiguration.zoomError) {
            return elementResolution.WARNING;
        }

        return elementResolution.ERROR;
    }

    public static isAnUneditedText(element: IEditorTextElement):boolean {
        const adjustedTextSize = (element as any).adjustedTextSize;
        return (!element.edited &&
            ((adjustedTextSize && element.text == (SF.Model.DefaultText as any)[adjustedTextSize]) ||
            (element.placeHolderTranslations && element.text == element.placeHolderTranslations[SF.currentLanguage]) ||
            Object.keys(SF.Model.DefaultText).some(size => {
                return (SF.Model.DefaultText as any)[size] === element.text;
            })));
    }

}

// expose
SF.Model.ElementError = elementError;

SF.Util = SF.Util || {} as IEditorUtil;
SF.Util.ErrorDetector = new ErrorDetector();

